// Первый экран главной «три двери» — тексты на трёх языках из ТЗ владельца
// (09.10.2026). AZ — на вычитку носителем. Названия предметов и районов — НЕ
// здесь, а из map-taxonomy.json (тот же справочник, что на /tutors); здесь
// только короткие подписи чипов «часто ищут», где ТЗ даёт их короче справочника.
import type { PartnerKind } from "@/data/map-taxonomy";
import type { HomeLang } from "./HomePage";

type DoorsCopy = {
  h1: string;
  h1Tail: string;
  test: {
    title: string;
    titleTail: string;
    /** Короткий заголовок для телефона. */
    titleShort: string;
    sub: string;
    subShort: string;
    cta: string;
    photoAlt: string;
  };
  abroad: { badge: string; title: string; text: string; cta: string; photoAlt: string };
  map: {
    title: string;
    titleTail: string;
    layers: Record<PartnerKind, string>;
    layerGroup: string;
    subject: Record<PartnerKind, string>;
    district: string;
    /** Строка-подсказка под заголовком — по слою (ТЗ «два пути»). */
    hint: Record<PartnerKind, string>;
    /** Кнопка перехода на карту /tutors — по слою. */
    search: Record<PartnerKind, string>;
    chipsLabel: string;
    openFull: string;
    emptyText: string;
    emptyCta: string;
    /** Счётчик — только при N ≥ 30 (ТЗ, «честность»). */
    count: (n: number, layer: PartnerKind) => string;
    perMonth: string;
    close: string;
    mapLabel: string;
  };
  /** Карточка для репетиторов под картой → /tutors/join. */
  join: { title: string; text: string; cta: string; photoAlt: string };
};

const ruPlural = (n: number, one: string, few: string, many: string) => {
  const a = n % 10;
  const b = n % 100;
  if (a === 1 && b !== 11) return one;
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return few;
  return many;
};

export const DOORS_COPY: Record<HomeLang, DoorsCopy> = {
  az: {
    h1: "Uşağınızın təhsili —",
    h1Tail: "bir yerdə",
    test: {
      title: "Kim olacaq?",
      titleTail: "Karyera testi",
      titleShort: "Kim olacaq?",
      sub: "10–22 yaş üçün · 10 səhifəlik hesabat: güclü tərəflər, peşələr və növbəti addımlar",
      subShort: "10–22 yaş üçün karyera testi və 10 səhifəlik hesabat",
      cta: "Testdən keç",
      photoAlt: "European Azerbaijan School sinfi Yolutap testindən keçir",
    },
    abroad: {
      badge: "pulsuz",
      title: "Xaricdə təhsil",
      text: "Müxtəlif ölkələrin universitetləri: qiymət, tələblər və müraciət müddətləri",
      cta: "Universitet seç",
      photoAlt: "Yuxarı sinif şagirdi noutbuka baxır",
    },
    map: {
      title: "Repetitorlar və dərnəklər",
      titleTail: "evə yaxın",
      layers: { tutor: "Repetitorlar", club: "Dərnəklər və bölmələr" },
      layerGroup: "Kimi axtarırsınız",
      subject: { tutor: "Fənn", club: "İstiqamət" },
      district: "Rayon",
      // Список кружков называется «İstiqamət», а не «Fənn» — слово в подсказке
      // по подписи списка (ТЗ давало «Fənni» для обоих слоёв).
      hint: {
        tutor: "Fənni və rayonu seçin — evinizə yaxın repetitorları göstərək",
        club: "İstiqaməti və rayonu seçin — evinizə yaxın dərnəkləri göstərək",
      },
      search: { tutor: "Repetitor tap", club: "Dərnək tap" },
      chipsLabel: "Tez-tez axtarılır",
      openFull: "Xəritəni tam aç",
      emptyText: "Xəritə yenidir. Kimi axtardığınızı yazın — seçib sizə zəng edəcəyik",
      emptyCta: "Müraciət qoy",
      count: (n, layer) => (layer === "tutor" ? `${n} repetitor xəritədə` : `${n} dərnək və bölmə xəritədə`),
      perMonth: "/ay",
      close: "Bağla",
      mapLabel: "Repetitor və dərnək xəritəsi",
    },
    join: {
      title: "Repetitor və ya dərnəksiniz?",
      text: "Pulsuz anket yaradın — valideynlər sizi xəritədə tapsın",
      cta: "Anket yarat",
      photoAlt: "Müəllim sinifdə şagirdlərlə",
    },
  },
  ru: {
    h1: "Учёба ребёнка —",
    h1Tail: "в одном месте",
    test: {
      title: "Кем стать?",
      titleTail: "Карьерный тест",
      titleShort: "Кем стать?",
      sub: "Для 10–22 лет · отчёт на 10 страниц: сильные стороны, профессии и что делать дальше",
      subShort: "Карьерный тест для 10–22 лет и отчёт на 10 страниц",
      cta: "Пройти тест",
      photoAlt: "Класс European Azerbaijan School проходит тест Yolutap",
    },
    abroad: {
      badge: "бесплатно",
      title: "Учёба за рубежом",
      text: "Вузы разных стран: стоимость, требования и сроки подачи",
      cta: "Подобрать вуз",
      photoAlt: "Старшеклассница смотрит в ноутбук",
    },
    map: {
      title: "Репетиторы и кружки",
      titleTail: "рядом с домом",
      layers: { tutor: "Репетиторы", club: "Кружки и секции" },
      layerGroup: "Кого вы ищете",
      subject: { tutor: "Предмет", club: "Направление" },
      district: "Район",
      hint: {
        tutor: "Выберите предмет и район — покажем репетиторов рядом с домом",
        club: "Выберите направление и район — покажем кружки рядом с домом",
      },
      search: { tutor: "Найти репетитора", club: "Найти кружок" },
      chipsLabel: "Часто ищут",
      openFull: "Открыть карту целиком",
      emptyText: "Карта новая. Напишите, кого ищете — подберём и позвоним",
      emptyCta: "Оставить заявку",
      count: (n, layer) =>
        layer === "tutor"
          ? `${n} ${ruPlural(n, "репетитор", "репетитора", "репетиторов")} на карте`
          : `${n} ${ruPlural(n, "кружок и секция", "кружка и секции", "кружков и секций")} на карте`,
      perMonth: "/мес",
      close: "Закрыть",
      mapLabel: "Карта репетиторов и кружков",
    },
    join: {
      title: "Вы репетитор или кружок?",
      text: "Заведите бесплатную анкету — родители найдут вас на карте",
      cta: "Создать анкету",
      photoAlt: "Учитель с учениками в классе",
    },
  },
  en: {
    h1: "Your child’s education —",
    h1Tail: "all in one place",
    test: {
      title: "Which career fits?",
      titleTail: "Career test",
      titleShort: "Which career fits?",
      sub: "Ages 10–22 · a 10-page report: strengths, careers and next steps",
      subShort: "Career test for ages 10–22 and a 10-page report",
      cta: "Take the test",
      photoAlt: "A European Azerbaijan School class takes the Yolutap test",
    },
    abroad: {
      badge: "free",
      title: "Study abroad",
      text: "Universities in different countries: costs, requirements and deadlines",
      cta: "Find a university",
      photoAlt: "A high-school student looks at her laptop",
    },
    map: {
      title: "Tutors and clubs",
      titleTail: "near home",
      layers: { tutor: "Tutors", club: "Clubs and classes" },
      layerGroup: "Who are you looking for",
      subject: { tutor: "Subject", club: "Activity" },
      district: "District",
      hint: {
        tutor: "Pick a subject and district — we’ll show tutors near your home",
        club: "Pick an activity and district — we’ll show clubs near your home",
      },
      search: { tutor: "Find a tutor", club: "Find a club" },
      chipsLabel: "Popular searches",
      openFull: "Open the full map",
      emptyText: "The map is new. Tell us who you need — we’ll find someone and call you",
      emptyCta: "Leave a request",
      count: (n, layer) => (layer === "tutor" ? `${n} tutors on the map` : `${n} clubs and classes on the map`),
      perMonth: "/mo",
      close: "Close",
      mapLabel: "Map of tutors and clubs",
    },
    join: {
      title: "Are you a tutor or a club?",
      text: "Create a free profile — parents will find you on the map",
      cta: "Create profile",
      photoAlt: "A teacher with students in class",
    },
  },
};

/** Порог счётчика «N репетиторов на карте»: меньше — счётчика нет вовсе. */
export const MAP_COUNT_MIN = 30;

/**
 * Чипы «часто ищут» — коды из map-taxonomy.json (verify-home-doors.mts
 * проверяет, что каждый код есть в справочнике своего слоя, а подпись — его
 * подпись или короче, как в ТЗ). Подписи записаны здесь, а не берутся из
 * справочника, чтобы сам справочник (16 КБ) не попадал в основной бандл
 * главной: он грузится лениво, только для выпадающих списков.
 */
export const MAP_CHIPS: Record<PartnerKind, { code: string; label: Record<HomeLang, string> }[]> = {
  tutor: [
    { code: "math", label: { az: "Riyaziyyat", ru: "Математика", en: "Maths" } },
    { code: "en", label: { az: "İngilis dili", ru: "Английский", en: "English" } },
    { code: "physics", label: { az: "Fizika", ru: "Физика", en: "Physics" } },
    { code: "homework", label: { az: "Ev tapşırıqlarına kömək", ru: "Помощь с домашним заданием", en: "Homework help" } },
    { code: "dim", label: { az: "DİM", ru: "DİM", en: "DİM" } },
  ],
  club: [
    { code: "robotics", label: { az: "Robototexnika", ru: "Робототехника", en: "Robotics" } },
    { code: "coding", label: { az: "Proqramlaşdırma", ru: "Программирование", en: "Programming" } },
    { code: "chess", label: { az: "Şahmat", ru: "Шахматы", en: "Chess" } },
    { code: "music", label: { az: "Musiqi", ru: "Музыка", en: "Music" } },
    { code: "drawing", label: { az: "Rəsm", ru: "Рисование", en: "Drawing" } },
  ],
};

/**
 * Фото двери «тест» — главный элемент первого экрана (LCP). Один источник для
 * <img> и для <link rel="preload"> в home-head.ts: иначе браузер скачал бы
 * кадр дважды. Только 640, 768 и 960 (31, 35 и 55 КБ): 1280 (83 КБ) на карточке
 * шириной ≤ 534px не нужен даже при dpr 2. 768 — под Lighthouse-телефон (412px,
 * dpr 1,75 → ~650px): иначе он брал бы 960. sizes — ширина карточки: на
 * десктопе ≤ 534px, в одну колонку (≤ 980px) — экран минус поля 2×20px.
 */
export const TEST_PHOTO = {
  src: "/photos/home/eas-class-wide-640.webp",
  srcSet: "/photos/home/eas-class-wide-640.webp 640w, /photos/home/eas-class-wide-768.webp 768w, /photos/home/eas-class-wide-960.webp 960w",
  sizes: "(max-width: 980px) calc(100vw - 40px), 534px",
  width: 1280,
  height: 686,
} as const;
